import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Layout, BookOpen, Users, Award, BarChart2, Settings,
  ClipboardList, PieChart, Activity, AlertTriangle, Pause, UserPlus, ChevronRight,
  Plus, Download, Eye, Edit, Trash2, GraduationCap, Clock, CheckCircle, X,
  Calendar, Star, Target, FileText, TrendingUp, Video, Play, SkipForward,
  ThumbsUp, MessageSquare, Filter, Search, Bell, Crown, Trophy, Medal, Badge,
  MapPin, Building, Globe, Phone, Mail, ExternalLink, Printer, Share2, Heart,
  Zap, Lightbulb, BookMarked, Briefcase, ArrowUpRight
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

// Import RechartsPieChart to avoid naming conflict
import { PieChart as RechartsPieChart, Pie, Cell } from 'recharts';

// Modal Components
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const NewCourseModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructor: '',
    duration: '',
    level: 'beginner',
    category: '',
    maxEnrollment: '',
    startDate: '',
    endDate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create New Course">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructor</label>
            <input
              type="text"
              value={formData.instructor}
              onChange={(e) => setFormData({...formData, instructor: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <input
              type="text"
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: e.target.value})}
              placeholder="e.g., 4 hours"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
            <select
              value={formData.level}
              onChange={(e) => setFormData({...formData, level: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Enrollment</label>
            <input
              type="number"
              value={formData.maxEnrollment}
              onChange={(e) => setFormData({...formData, maxEnrollment: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({...formData, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({...formData, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Create Course
          </button>
        </div>
      </form>
    </Modal>
  );
};

const AddLearnerModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    position: '',
    phone: '',
    manager: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Add New Learner">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <select
              value={formData.department}
              onChange={(e) => setFormData({...formData, department: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              <option value="">Select Department</option>
              <option value="Sales">Sales</option>
              <option value="Marketing">Marketing</option>
              <option value="IT">IT</option>
              <option value="HR">HR</option>
              <option value="Finance">Finance</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
            <input
              type="text"
              value={formData.position}
              onChange={(e) => setFormData({...formData, position: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
            <input
              type="text"
              value={formData.manager}
              onChange={(e) => setFormData({...formData, manager: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Add Learner
          </button>
        </div>
      </form>
    </Modal>
  );
};

const IssueCertificateModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    learner: '',
    course: '',
    score: '',
    issueDate: '',
    expiryDate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Issue Certificate">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Learner</label>
            <select
              value={formData.learner}
              onChange={(e) => setFormData({...formData, learner: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              <option value="">Select Learner</option>
              <option value="alex">Alex Thompson</option>
              <option value="maria">Maria Garcia</option>
              <option value="david">David Kim</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
            <select
              value={formData.course}
              onChange={(e) => setFormData({...formData, course: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              <option value="">Select Course</option>
              <option value="sales">Advanced Sales Techniques</option>
              <option value="service">Customer Service Excellence</option>
              <option value="leadership">Leadership Fundamentals</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Score (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.score}
              onChange={(e) => setFormData({...formData, score: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
            <input
              type="date"
              value={formData.issueDate}
              onChange={(e) => setFormData({...formData, issueDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
            <input
              type="date"
              value={formData.expiryDate}
              onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Issue Certificate
          </button>
        </div>
      </form>
    </Modal>
  );
};

const CourseDetailModal = ({ course, onClose }) => {
  return (
    <Modal isOpen={true} onClose={onClose} title={course.title}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-medium text-gray-900">Course Information</h3>
            <div className="mt-2 space-y-2">
              <p><span className="text-gray-500">Instructor:</span> {course.instructor}</p>
              <p><span className="text-gray-500">Duration:</span> {course.duration}</p>
              <p><span className="text-gray-500">Level:</span> {course.level}</p>
              <p><span className="text-gray-500">Category:</span> {course.category}</p>
              <p><span className="text-gray-500">Status:</span> 
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  course.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {course.status}
                </span>
              </p>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Enrollment Statistics</h3>
            <div className="mt-2 space-y-2">
              <p><span className="text-gray-500">Enrolled:</span> {course.enrolled}</p>
              <p><span className="text-gray-500">Completion Rate:</span> {course.completion}%</p>
            </div>
          </div>
        </div>
        
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-900 mb-2">Course Modules</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span>Module 1: Introduction</span>
              <span className="text-sm text-gray-500">30 min</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span>Module 2: Core Concepts</span>
              <span className="text-sm text-gray-500">45 min</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span>Module 3: Practical Application</span>
              <span className="text-sm text-gray-500">60 min</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span>Module 4: Assessment</span>
              <span className="text-sm text-gray-500">30 min</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

const LearnerProfileModal = ({ learner, onClose }) => {
  return (
    <Modal isOpen={true} onClose={onClose} title={`${learner.name} - Profile`}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-medium text-gray-900">Personal Information</h3>
            <div className="mt-2 space-y-2">
              <p><span className="text-gray-500">Name:</span> {learner.name}</p>
              <p><span className="text-gray-500">Email:</span> {learner.email}</p>
              <p><span className="text-gray-500">Department:</span> {learner.department}</p>
              <p><span className="text-gray-500">Status:</span> 
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  learner.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {learner.status}
                </span>
              </p>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Learning Statistics</h3>
            <div className="mt-2 space-y-2">
              <p><span className="text-gray-500">Courses Enrolled:</span> {learner.coursesEnrolled}</p>
              <p><span className="text-gray-500">Courses Completed:</span> {learner.completedCourses}</p>
              <p><span className="text-gray-500">Average Score:</span> {learner.avgScore}%</p>
            </div>
          </div>
        </div>
        
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-900 mb-2">Current Courses</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span>Advanced Sales Techniques</span>
              <span className="text-sm text-green-600">75% Complete</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span>Customer Service Excellence</span>
              <span className="text-sm text-green-600">100% Complete</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span>Leadership Fundamentals</span>
              <span className="text-sm text-yellow-600">45% Complete</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

const CertificateModal = ({ certificate, onClose }) => {
  return (
    <Modal isOpen={true} onClose={onClose} title="Certificate Details">
      <div className="space-y-6">
        <div className="text-center p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
          <Award className="w-16 h-16 text-purple-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Certificate of Completion</h2>
          <p className="text-lg text-gray-700 mb-4">This is to certify that</p>
          <p className="text-xl font-semibold text-purple-600 mb-4">{certificate.learnerName}</p>
          <p className="text-gray-700 mb-4">has successfully completed the course</p>
          <p className="text-lg font-semibold text-gray-900 mb-4">{certificate.courseName}</p>
          <p className="text-gray-600">with a score of <span className="font-semibold text-purple-600">{certificate.score}%</span></p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-medium text-gray-900">Certificate Information</h3>
            <div className="mt-2 space-y-2">
              <p><span className="text-gray-500">Issued Date:</span> {certificate.issuedDate}</p>
              <p><span className="text-gray-500">Expiry Date:</span> {certificate.expiryDate}</p>
              <p><span className="text-gray-500">Status:</span> 
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  certificate.status === 'valid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {certificate.status}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={() => alert('Downloading certificate...')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4 inline mr-2" />
            Download
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

const BloomDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewCourseModal, setShowNewCourseModal] = useState(false);
  const [showAddLearnerModal, setShowAddLearnerModal] = useState(false);
  const [showIssueCertificateModal, setShowIssueCertificateModal] = useState(false);
  const [showCourseDetailModal, setShowCourseDetailModal] = useState(false);
  const [showLearnerProfileModal, setShowLearnerProfileModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const mainTabs = [
    { id: 'overview', label: 'Overview', icon: Layout },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'learners', label: 'Learners', icon: Users },
    { id: 'certificates', label: 'Certificates', icon: Award },
    { id: 'sessions', label: 'Sessions', icon: Calendar },
    { id: 'recognition', label: 'Recognition', icon: Star },
    { id: 'quality', label: 'Quality Club', icon: Target },
    { id: 'resources', label: 'Resources', icon: FileText },
    { id: 'growth', label: 'Growth', icon: TrendingUp }
  ];

  const moreTabs = [
    { id: 'surveys', label: 'Surveys', icon: ClipboardList },
    { id: 'reports', label: 'Reports', icon: BarChart2 },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Mock data
  const stats = {
    totalCourses: 24,
    activeLearners: 156,
    completedCertificates: 89,
    avgCompletionRate: 78,
    upcomingSessions: 12,
    pendingApprovals: 5
  };

  const courses = [
    {
      id: 1,
      title: "Advanced Sales Techniques",
      instructor: "Sarah Johnson",
      duration: "4 hours",
      level: "Intermediate",
      enrolled: 45,
      completion: 78,
      status: "active",
      category: "Sales"
    },
    {
      id: 2,
      title: "Customer Service Excellence",
      instructor: "Mike Chen",
      duration: "3 hours",
      level: "Beginner",
      enrolled: 67,
      completion: 92,
      status: "active",
      category: "Customer Service"
    },
    {
      id: 3,
      title: "Leadership Fundamentals",
      instructor: "Emily Davis",
      duration: "6 hours",
      level: "Advanced",
      enrolled: 23,
      completion: 65,
      status: "draft",
      category: "Leadership"
    }
  ];

  const learners = [
    {
      id: 1,
      name: "Alex Thompson",
      email: "alex.thompson@company.com",
      department: "Sales",
      coursesEnrolled: 4,
      completedCourses: 3,
      avgScore: 87,
      status: "active"
    },
    {
      id: 2,
      name: "Maria Garcia",
      email: "maria.garcia@company.com",
      department: "Marketing",
      coursesEnrolled: 6,
      completedCourses: 5,
      avgScore: 92,
      status: "active"
    },
    {
      id: 3,
      name: "David Kim",
      email: "david.kim@company.com",
      department: "IT",
      coursesEnrolled: 3,
      completedCourses: 2,
      avgScore: 78,
      status: "inactive"
    }
  ];

  const certificates = [
    {
      id: 1,
      learnerName: "Alex Thompson",
      courseName: "Advanced Sales Techniques",
      issuedDate: "2024-03-10",
      expiryDate: "2025-03-10",
      status: "valid",
      score: 87
    },
    {
      id: 2,
      learnerName: "Maria Garcia",
      courseName: "Customer Service Excellence",
      issuedDate: "2024-03-08",
      expiryDate: "2025-03-08",
      status: "valid",
      score: 92
    },
    {
      id: 3,
      learnerName: "David Kim",
      courseName: "Leadership Fundamentals",
      issuedDate: "2024-02-28",
      expiryDate: "2025-02-28",
      status: "expired",
      score: 78
    }
  ];

  const recentActivity = [
    {
      id: 1,
      type: "course_completed",
      user: "Alex Thompson",
      course: "Advanced Sales Techniques",
      time: "2 hours ago",
      score: 87
    },
    {
      id: 2,
      type: "certificate_issued",
      user: "Maria Garcia",
      course: "Customer Service Excellence",
      time: "4 hours ago",
      score: 92
    },
    {
      id: 3,
      type: "course_enrolled",
      user: "David Kim",
      course: "Leadership Fundamentals",
      time: "1 day ago"
    }
  ];

  const enrollmentData = [
    { month: 'Jan', enrollments: 45 },
    { month: 'Feb', enrollments: 52 },
    { month: 'Mar', enrollments: 67 },
    { month: 'Apr', enrollments: 78 },
    { month: 'May', enrollments: 89 },
    { month: 'Jun', enrollments: 95 }
  ];

  const courseCategories = [
    { name: 'Sales', value: 35, color: '#8B5CF6' },
    { name: 'Customer Service', value: 25, color: '#A855F7' },
    { name: 'Leadership', value: 20, color: '#C084FC' },
    { name: 'Technical', value: 15, color: '#DDD6FE' },
    { name: 'Other', value: 5, color: '#F3E8FF' }
  ];

  const completionRates = [
    { department: 'Sales', rate: 85 },
    { department: 'Marketing', rate: 78 },
    { department: 'IT', rate: 92 },
    { department: 'HR', rate: 88 },
    { department: 'Finance', rate: 75 }
  ];

  const avgScores = [
    { course: 'Sales Techniques', score: 87 },
    { course: 'Customer Service', score: 92 },
    { course: 'Leadership', score: 78 },
    { course: 'Technical Skills', score: 85 },
    { course: 'Communication', score: 90 }
  ];

  // Additional mock data for new features
  const sessions = [
    {
      id: 1,
      title: "Advanced Sales Workshop",
      instructor: "Sarah Johnson",
      date: "2024-03-20",
      time: "09:00 AM",
      duration: "4 hours",
      location: "Training Room A",
      capacity: 25,
      enrolled: 18,
      status: "open",
      type: "workshop"
    },
    {
      id: 2,
      title: "Customer Service Excellence",
      instructor: "Mike Chen",
      date: "2024-03-22",
      time: "02:00 PM",
      duration: "3 hours",
      location: "Virtual",
      capacity: 50,
      enrolled: 35,
      status: "open",
      type: "virtual"
    },
    {
      id: 3,
      title: "Leadership Fundamentals",
      instructor: "Emily Davis",
      date: "2024-03-25",
      time: "10:00 AM",
      duration: "6 hours",
      location: "Conference Hall",
      capacity: 30,
      enrolled: 30,
      status: "full",
      type: "seminar"
    }
  ];

  const recognitionAchievements = [
    {
      id: 1,
      employee: "Alex Thompson",
      achievement: "Top Performer - Q1 2024",
      category: "Sales Excellence",
      date: "2024-03-15",
      points: 500,
      badge: "gold",
      description: "Exceeded sales targets by 150%"
    },
    {
      id: 2,
      employee: "Maria Garcia",
      achievement: "Customer Service Champion",
      category: "Service Excellence",
      date: "2024-03-10",
      points: 300,
      badge: "silver",
      description: "Received 50+ positive customer reviews"
    },
    {
      id: 3,
      employee: "David Kim",
      achievement: "Innovation Award",
      category: "Technical Excellence",
      date: "2024-03-08",
      points: 400,
      badge: "bronze",
      description: "Implemented new automation system"
    }
  ];

  const qualityTools = [
    {
      id: 1,
      name: "Quality Assessment Checklist",
      category: "QA Tools",
      description: "Comprehensive checklist for quality assurance",
      version: "2.1",
      lastUpdated: "2024-03-15",
      downloads: 45,
      rating: 4.8
    },
    {
      id: 2,
      name: "Process Improvement Guide",
      category: "Process",
      description: "Step-by-step guide for process optimization",
      version: "1.5",
      lastUpdated: "2024-03-10",
      downloads: 32,
      rating: 4.6
    },
    {
      id: 3,
      name: "Quality Metrics Dashboard",
      category: "Analytics",
      description: "Real-time quality metrics and reporting",
      version: "3.0",
      lastUpdated: "2024-03-12",
      downloads: 28,
      rating: 4.9
    }
  ];

  const resources = [
    {
      id: 1,
      name: "Employee Handbook 2024",
      category: "Policies",
      type: "PDF",
      size: "2.5 MB",
      downloads: 156,
      lastUpdated: "2024-01-15"
    },
    {
      id: 2,
      name: "Safety Guidelines",
      category: "Safety",
      type: "PDF",
      size: "1.8 MB",
      downloads: 89,
      lastUpdated: "2024-02-20"
    },
    {
      id: 3,
      name: "Training Video Library",
      category: "Training",
      type: "Video",
      size: "500 MB",
      downloads: 234,
      lastUpdated: "2024-03-01"
    }
  ];

  const growthPaths = [
    {
      id: 1,
      title: "Sales Career Path",
      level: "Entry to Senior",
      duration: "2-3 years",
      courses: ["Sales Fundamentals", "Advanced Sales", "Sales Management"],
      skills: ["Communication", "Negotiation", "CRM"],
      opportunities: ["Sales Representative", "Sales Manager", "Sales Director"]
    },
    {
      id: 2,
      title: "Technical Career Path",
      level: "Junior to Lead",
      duration: "3-4 years",
      courses: ["Technical Fundamentals", "Advanced Programming", "System Architecture"],
      skills: ["Programming", "Problem Solving", "System Design"],
      opportunities: ["Developer", "Senior Developer", "Technical Lead"]
    },
    {
      id: 3,
      title: "Management Career Path",
      level: "Team Lead to Director",
      duration: "4-5 years",
      courses: ["Leadership Fundamentals", "Strategic Management", "Executive Leadership"],
      skills: ["Leadership", "Strategic Thinking", "Team Management"],
      opportunities: ["Team Lead", "Manager", "Director"]
    }
  ];

  const surveys = [
    {
      id: 1,
      title: "Course Satisfaction Survey",
      status: "active",
      responses: 45,
      totalParticipants: 67,
      endDate: "2024-03-25",
      questions: 10
    },
    {
      id: 2,
      title: "Training Program Feedback",
      status: "active",
      responses: 23,
      totalParticipants: 34,
      endDate: "2024-03-30",
      questions: 15
    },
    {
      id: 3,
      title: "Instructor Evaluation",
      status: "completed",
      responses: 89,
      totalParticipants: 89,
      endDate: "2024-03-15",
      questions: 8
    }
  ];

  const courseApprovals = [
    {
      id: 1,
      course: "Advanced Data Analytics",
      requester: "John Smith",
      department: "IT",
      requestDate: "2024-03-18",
      status: "pending",
      priority: "high"
    },
    {
      id: 2,
      course: "Customer Experience Design",
      requester: "Lisa Brown",
      department: "Marketing",
      requestDate: "2024-03-17",
      status: "approved",
      priority: "medium"
    },
    {
      id: 3,
      course: "Financial Management",
      requester: "Robert Wilson",
      department: "Finance",
      requestDate: "2024-03-16",
      status: "rejected",
      priority: "low"
    }
  ];

  const examResults = [
    {
      id: 1,
      learner: "Alex Thompson",
      course: "Advanced Sales Techniques",
      examDate: "2024-03-15",
      score: 87,
      passingScore: 70,
      timeTaken: "45 min",
      attempts: 1,
      status: "passed"
    },
    {
      id: 2,
      learner: "Maria Garcia",
      course: "Customer Service Excellence",
      examDate: "2024-03-14",
      score: 92,
      passingScore: 70,
      timeTaken: "38 min",
      attempts: 1,
      status: "passed"
    },
    {
      id: 3,
      learner: "David Kim",
      course: "Leadership Fundamentals",
      examDate: "2024-03-13",
      score: 65,
      passingScore: 70,
      timeTaken: "55 min",
      attempts: 2,
      status: "failed"
    }
  ];

  const ksaStats = {
    regions: [
      { region: "Riyadh", employees: 45, courses: 12, completion: 85 },
      { region: "Jeddah", employees: 38, courses: 10, completion: 78 },
      { region: "Dammam", employees: 32, courses: 8, completion: 82 },
      { region: "Mecca", employees: 28, courses: 6, completion: 75 },
      { region: "Medina", employees: 25, courses: 5, completion: 80 }
    ],
    departments: [
      { dept: "Sales", employees: 35, avgScore: 87, certificates: 28 },
      { dept: "Marketing", employees: 22, avgScore: 92, certificates: 18 },
      { dept: "IT", employees: 18, avgScore: 85, certificates: 15 },
      { dept: "HR", employees: 12, avgScore: 89, certificates: 10 },
      { dept: "Finance", employees: 15, avgScore: 91, certificates: 12 }
    ]
  };

  // Component functions
  const QuickActionButton = ({ icon: Icon, label, onClick, color = "purple" }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 bg-${color}-100 text-${color}-700 rounded-lg hover:bg-${color}-200 transition-colors font-medium`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </motion.button>
  );

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "purple" }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`p-2 bg-${color}-100 rounded-lg`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
          <div className="ml-3">
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
      </div>
      {subtitle && (
        <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
      )}
    </motion.div>
  );

  const CourseCard = ({ course }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{course.title}</h3>
          <p className="text-sm text-gray-500">by {course.instructor}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          course.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {course.status}
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">Duration</p>
          <p className="font-medium">{course.duration}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Level</p>
          <p className="font-medium">{course.level}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Enrolled</p>
          <p className="font-medium">{course.enrolled}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Completion</p>
          <p className="font-medium">{course.completion}%</p>
        </div>
      </div>

             <div className="flex gap-2">
         <button 
           onClick={() => handleEditCourse(course)}
           className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
         >
           <Eye className="w-4 h-4 inline mr-1" />
           View
         </button>
         <button 
           onClick={() => handleEditCourse(course)}
           className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
         >
           <Edit className="w-4 h-4" />
         </button>
         <button 
           onClick={() => handleDeleteCourse(course.id)}
           className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
         >
           <Trash2 className="w-4 h-4" />
         </button>
       </div>
    </motion.div>
  );

  const LearnerCard = ({ learner }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{learner.name}</h3>
          <p className="text-sm text-gray-500">{learner.email}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          learner.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {learner.status}
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">Department</p>
          <p className="font-medium">{learner.department}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Avg Score</p>
          <p className="font-medium">{learner.avgScore}%</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Enrolled</p>
          <p className="font-medium">{learner.coursesEnrolled}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Completed</p>
          <p className="font-medium">{learner.completedCourses}</p>
        </div>
      </div>

             <div className="flex gap-2">
         <button 
           onClick={() => handleViewProfile(learner)}
           className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
         >
           <Eye className="w-4 h-4 inline mr-1" />
           View Profile
         </button>
         <button 
           onClick={() => handleViewProfile(learner)}
           className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
         >
           <Edit className="w-4 h-4" />
         </button>
       </div>
    </motion.div>
  );

  const CertificateCard = ({ certificate }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{certificate.courseName}</h3>
          <p className="text-sm text-gray-500">Issued to {certificate.learnerName}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          certificate.status === 'valid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {certificate.status}
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">Issued</p>
          <p className="font-medium">{certificate.issuedDate}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Expires</p>
          <p className="font-medium">{certificate.expiryDate}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Score</p>
          <p className="font-medium">{certificate.score}%</p>
        </div>
      </div>

             <div className="flex gap-2">
         <button 
           onClick={() => handleViewCertificate(certificate)}
           className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
         >
           <Eye className="w-4 h-4 inline mr-1" />
           View
         </button>
         <button 
           onClick={() => handleDownloadCertificate(certificate.id)}
           className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
         >
           <Download className="w-4 h-4" />
         </button>
       </div>
    </motion.div>
  );

  const RecentActivity = ({ activities }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
      <div className="space-y-3">
        {activities.map((activity) => (
          <div key={activity.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="p-2 bg-purple-100 rounded-lg">
              {activity.type === 'course_completed' && <CheckCircle className="w-4 h-4 text-purple-600" />}
              {activity.type === 'certificate_issued' && <Award className="w-4 h-4 text-purple-600" />}
              {activity.type === 'course_enrolled' && <BookOpen className="w-4 h-4 text-purple-600" />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{activity.user}</p>
              <p className="text-xs text-gray-500">
                {activity.type === 'course_completed' && `Completed ${activity.course}`}
                {activity.type === 'certificate_issued' && `Received certificate for ${activity.course}`}
                {activity.type === 'course_enrolled' && `Enrolled in ${activity.course}`}
                {activity.score && ` (Score: ${activity.score}%)`}
              </p>
            </div>
            <span className="text-xs text-gray-400">{activity.time}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // Tab content components
  const OverviewContent = () => (
    <div className="space-y-6">
      {/* KSA Regional Stats */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">KSA Regional Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {ksaStats.regions.map((region, index) => (
            <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900">{region.region}</h4>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-600">{region.employees} employees</p>
                <p className="text-sm text-gray-600">{region.courses} courses</p>
                <p className="text-sm font-medium text-purple-600">{region.completion}% completion</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Total Courses"
          value={stats.totalCourses}
          subtitle="Active learning programs"
          icon={BookOpen}
          color="purple"
        />
        <StatCard
          title="Active Learners"
          value={stats.activeLearners}
          subtitle="Currently enrolled"
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Certificates Issued"
          value={stats.completedCertificates}
          subtitle="Successfully completed"
          icon={Award}
          color="green"
        />
        <StatCard
          title="Completion Rate"
          value={`${stats.avgCompletionRate}%`}
          subtitle="Average across all courses"
          icon={CheckCircle}
          color="orange"
        />
        <StatCard
          title="Upcoming Sessions"
          value={stats.upcomingSessions}
          subtitle="Scheduled this week"
          icon={Clock}
          color="indigo"
        />
        <StatCard
          title="Pending Approvals"
          value={stats.pendingApprovals}
          subtitle="Awaiting review"
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Enrollment Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={enrollmentData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="enrollments" stroke="#8B5CF6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Categories</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={courseCategories}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {courseCategories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Department Performance */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {ksaStats.departments.map((dept, index) => (
            <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900">{dept.dept}</h4>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-600">{dept.employees} employees</p>
                <p className="text-sm font-medium text-green-600">{dept.avgScore}% avg score</p>
                <p className="text-sm text-purple-600">{dept.certificates} certificates</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <RecentActivity activities={recentActivity} />
    </div>
  );

  const CoursesContent = () => (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
                 <button 
           onClick={handleNewCourse}
           className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
         >
           <Plus className="w-4 h-4 inline mr-2" />
           New Course
         </button>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <CourseCard key={course.id} course={course} />
        ))}
      </div>
    </div>
  );

  const LearnersContent = () => (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search learners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
                 <button 
           onClick={handleAddLearner}
           className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
         >
           <UserPlus className="w-4 h-4 inline mr-2" />
           Add Learner
         </button>
      </div>

      {/* Learners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {learners.map((learner) => (
          <LearnerCard key={learner.id} learner={learner} />
        ))}
      </div>
    </div>
  );

  const CertificatesContent = () => (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search certificates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="valid">Valid</option>
          <option value="expired">Expired</option>
        </select>
                 <button 
           onClick={handleIssueCertificate}
           className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
         >
           <Award className="w-4 h-4 inline mr-2" />
           Issue Certificate
         </button>
      </div>

      {/* Certificates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {certificates.map((certificate) => (
          <CertificateCard key={certificate.id} certificate={certificate} />
        ))}
      </div>
    </div>
  );

  const ReportsContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Analytics & Reports</h2>
        <div className="flex gap-2">
          <QuickActionButton icon={Download} label="Export Data" onClick={() => {}} />
          <QuickActionButton icon={BarChart2} label="Generate Report" onClick={() => {}} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Completion Rates by Department</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={completionRates}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="department" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="rate" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Scores by Course</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={avgScores}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="course" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="score" fill="#A855F7" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const SessionsContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Training Sessions</h2>
        <button 
          onClick={handleScheduleSession}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4 inline mr-2" />
          Schedule Session
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sessions.map((session) => (
          <motion.div
            key={session.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{session.title}</h3>
                <p className="text-sm text-gray-500">by {session.instructor}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                session.status === 'open' ? 'bg-green-100 text-green-800' : 
                session.status === 'full' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {session.status}
              </span>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                {session.date} at {session.time}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-2" />
                {session.duration}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="w-4 h-4 mr-2" />
                {session.location}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Users className="w-4 h-4 mr-2" />
                {session.enrolled}/{session.capacity} enrolled
              </div>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleBookSession(session.id)}
                className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
              >
                <Calendar className="w-4 h-4 inline mr-1" />
                Book Session
              </button>
              <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const RecognitionContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Recognition Club</h2>
        <button 
          onClick={handleNominateEmployee}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Star className="w-4 h-4 inline mr-2" />
          Nominate Employee
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {recognitionAchievements.map((achievement) => (
          <motion.div
            key={achievement.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="text-center mb-4">
              <div className={`w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center ${
                achievement.badge === 'gold' ? 'bg-yellow-100' :
                achievement.badge === 'silver' ? 'bg-gray-100' : 'bg-orange-100'
              }`}>
                {achievement.badge === 'gold' ? <Crown className="w-8 h-8 text-yellow-600" /> :
                 achievement.badge === 'silver' ? <Medal className="w-8 h-8 text-gray-600" /> :
                 <Trophy className="w-8 h-8 text-orange-600" />}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{achievement.achievement}</h3>
              <p className="text-sm text-gray-500">{achievement.employee}</p>
            </div>
            
            <div className="space-y-2 mb-4">
              <p className="text-sm text-gray-600">{achievement.description}</p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Category:</span>
                <span className="font-medium">{achievement.category}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Points:</span>
                <span className="font-medium text-purple-600">{achievement.points}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date:</span>
                <span className="font-medium">{achievement.date}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleCongratulate(achievement.employee)}
                className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
              >
                <Heart className="w-4 h-4 inline mr-1" />
                Congratulate
              </button>
              <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const QualityContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Quality Club</h2>
        <button 
          onClick={handleAddQualityTool}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4 inline mr-2" />
          Add Tool
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {qualityTools.map((tool) => (
          <motion.div
            key={tool.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{tool.name}</h3>
                <p className="text-sm text-gray-500">{tool.category}</p>
              </div>
              <div className="flex items-center text-sm text-yellow-600">
                <Star className="w-4 h-4 mr-1" />
                {tool.rating}
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">{tool.description}</p>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Version:</span>
                <span className="font-medium">{tool.version}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Downloads:</span>
                <span className="font-medium">{tool.downloads}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Updated:</span>
                <span className="font-medium">{tool.lastUpdated}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium">
                <Download className="w-4 h-4 inline mr-1" />
                Download
              </button>
              <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const ResourcesContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Resource Channel</h2>
        <button 
          onClick={handleUploadResource}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4 inline mr-2" />
          Upload Resource
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resources.map((resource) => (
          <motion.div
            key={resource.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{resource.name}</h3>
                <p className="text-sm text-gray-500">{resource.category}</p>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                resource.type === 'PDF' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {resource.type}
              </div>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Size:</span>
                <span className="font-medium">{resource.size}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Downloads:</span>
                <span className="font-medium">{resource.downloads}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Updated:</span>
                <span className="font-medium">{resource.lastUpdated}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleDownloadResource(resource.name)}
                className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4 inline mr-1" />
                Download
              </button>
              <button 
                onClick={() => handlePrintResource(resource.name)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Printer className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const GrowthContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">How I Can Grow</h2>
        <button 
          onClick={handleCareerAssessment}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          Career Assessment
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {growthPaths.map((path) => (
          <motion.div
            key={path.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{path.title}</h3>
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Briefcase className="w-4 h-4 mr-2" />
                {path.level}
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="w-4 h-4 mr-2" />
                {path.duration}
              </div>
            </div>
            
            <div className="space-y-3 mb-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Required Courses:</h4>
                <div className="flex flex-wrap gap-1">
                  {path.courses.map((course, index) => (
                    <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                      {course}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Key Skills:</h4>
                <div className="flex flex-wrap gap-1">
                  {path.skills.map((skill, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Career Opportunities:</h4>
                <div className="flex flex-wrap gap-1">
                  {path.opportunities.map((opportunity, index) => (
                    <span key={index} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                      {opportunity}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleStartPath(path.title)}
                className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
              >
                <ArrowUpRight className="w-4 h-4 inline mr-1" />
                Start Path
              </button>
              <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const SurveysContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Surveys & Feedback</h2>
        <button 
          onClick={handleCreateSurvey}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4 inline mr-2" />
          Create Survey
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {surveys.map((survey) => (
          <motion.div
            key={survey.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{survey.title}</h3>
                <p className="text-sm text-gray-500">{survey.questions} questions</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                survey.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {survey.status}
              </span>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Responses:</span>
                <span className="font-medium">{survey.responses}/{survey.totalParticipants}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full" 
                  style={{ width: `${(survey.responses / survey.totalParticipants) * 100}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">End Date:</span>
                <span className="font-medium">{survey.endDate}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleTakeSurvey(survey.title)}
                className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
              >
                <MessageSquare className="w-4 h-4 inline mr-1" />
                Take Survey
              </button>
              <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <BarChart2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const SettingsContent = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">System Settings</h2>
      
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">General Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Auto-enrollment</p>
              <p className="text-sm text-gray-500">Automatically enroll new employees in required courses</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Certificate expiration alerts</p>
              <p className="text-sm text-gray-500">Send notifications when certificates are about to expire</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Progress tracking</p>
              <p className="text-sm text-gray-500">Track detailed progress for all learners</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  // Handler functions
  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setShowCourseDetailModal(true);
  };

  const handleDeleteCourse = (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      alert(`Course ${courseId} deleted successfully!`);
    }
  };

  const handleViewProfile = (learner) => {
    setSelectedLearner(learner);
    setShowLearnerProfileModal(true);
  };

  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setShowCertificateModal(true);
  };

  const handleDownloadCertificate = (certificateId) => {
    alert(`Downloading certificate ${certificateId}...`);
  };

  const handleNewCourse = () => {
    setShowNewCourseModal(true);
  };

  const handleAddLearner = () => {
    setShowAddLearnerModal(true);
  };

  const handleIssueCertificate = () => {
    setShowIssueCertificateModal(true);
  };

  const handleBookSession = (sessionId) => {
    alert(`Session ${sessionId} booked successfully!`);
  };

  const handleNominateEmployee = () => {
    alert('Employee nomination form opened!');
  };

  const handleAddQualityTool = () => {
    alert('Add quality tool form opened!');
  };

  const handleUploadResource = () => {
    alert('Resource upload form opened!');
  };

  const handleCareerAssessment = () => {
    alert('Career assessment started!');
  };

  const handleCreateSurvey = () => {
    alert('Survey creation form opened!');
  };

  const handleScheduleSession = () => {
    alert('Session scheduling form opened!');
  };

  const handleCongratulate = (employeeName) => {
    alert(`Congratulations message sent to ${employeeName}!`);
  };

  const handleDownloadResource = (resourceName) => {
    alert(`Downloading ${resourceName}...`);
  };

  const handlePrintResource = (resourceName) => {
    alert(`Printing ${resourceName}...`);
  };

  const handleTakeSurvey = (surveyName) => {
    alert(`Opening ${surveyName}...`);
  };

  const handleStartPath = (pathName) => {
    alert(`Starting career path: ${pathName}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Bloom Learning Academy</h1>
            <p className="text-purple-100 mt-1">
              Comprehensive learning management and employee development platform
            </p>
          </div>
          
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-sm font-medium backdrop-blur-sm hover:bg-white/20 transition-colors">
              <Download className="w-4 h-4 inline mr-2" />
              Export Data
            </button>
                         <button 
               onClick={handleNewCourse}
               className="px-4 py-2 bg-white text-purple-800 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2 font-medium"
             >
               <Plus className="w-5 h-5" />
               New Course
             </button>
          </div>
        </div>

                 {/* Centered Tabs */}
         <div className="mt-6 flex justify-center">
           <div className="inline-flex space-x-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
             {mainTabs.map((tab) => {
               const Icon = tab.icon;
               return (
                 <button
                   key={tab.id}
                   onClick={() => setActiveTab(tab.id)}
                                     className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-purple-800'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                 >
                   <Icon className="w-4 h-4" />
                   {tab.label}
                 </button>
               );
             })}
             
             {/* More Dropdown */}
             <div className="relative" ref={moreMenuRef}>
               <button
                 onClick={() => setShowMoreMenu(!showMoreMenu)}
                                   className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    moreTabs.some(tab => activeTab === tab.id)
                      ? 'bg-white text-purple-800'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
               >
                 <ChevronRight className={`w-4 h-4 transition-transform ${showMoreMenu ? 'rotate-90' : ''}`} />
                 More
               </button>
               
               {showMoreMenu && (
                 <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[150px] z-10">
                   {moreTabs.map((tab) => {
                     const Icon = tab.icon;
                     return (
                       <button
                         key={tab.id}
                         onClick={() => {
                           setActiveTab(tab.id);
                           setShowMoreMenu(false);
                         }}
                                                   className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors w-full text-left ${
                            activeTab === tab.id
                              ? 'bg-purple-100 text-purple-800'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                       >
                         <Icon className="w-4 h-4" />
                         {tab.label}
                       </button>
                     );
                   })}
                 </div>
               )}
             </div>
           </div>
         </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <OverviewContent />
          </motion.div>
        )}
        
        {activeTab === 'courses' && (
          <motion.div
            key="courses"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <CoursesContent />
          </motion.div>
        )}
        
        {activeTab === 'learners' && (
          <motion.div
            key="learners"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <LearnersContent />
          </motion.div>
        )}
        
                 {activeTab === 'certificates' && (
           <motion.div
             key="certificates"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <CertificatesContent />
           </motion.div>
         )}

         {activeTab === 'sessions' && (
           <motion.div
             key="sessions"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <SessionsContent />
           </motion.div>
         )}

         {activeTab === 'recognition' && (
           <motion.div
             key="recognition"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <RecognitionContent />
           </motion.div>
         )}

         {activeTab === 'quality' && (
           <motion.div
             key="quality"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <QualityContent />
           </motion.div>
         )}

         {activeTab === 'resources' && (
           <motion.div
             key="resources"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <ResourcesContent />
           </motion.div>
         )}

         {activeTab === 'growth' && (
           <motion.div
             key="growth"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <GrowthContent />
           </motion.div>
         )}

         {activeTab === 'surveys' && (
           <motion.div
             key="surveys"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <SurveysContent />
           </motion.div>
         )}
         
         {activeTab === 'reports' && (
           <motion.div
             key="reports"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <ReportsContent />
           </motion.div>
         )}
         
         {activeTab === 'settings' && (
           <motion.div
             key="settings"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.3 }}
           >
             <SettingsContent />
           </motion.div>
         )}
       </AnimatePresence>

       {/* Modals */}
       {showNewCourseModal && (
         <NewCourseModal 
           onClose={() => setShowNewCourseModal(false)}
           onSubmit={(courseData) => {
             alert('Course created successfully!');
             setShowNewCourseModal(false);
           }}
         />
       )}

       {showAddLearnerModal && (
         <AddLearnerModal 
           onClose={() => setShowAddLearnerModal(false)}
           onSubmit={(learnerData) => {
             alert('Learner added successfully!');
             setShowAddLearnerModal(false);
           }}
         />
       )}

       {showIssueCertificateModal && (
         <IssueCertificateModal 
           onClose={() => setShowIssueCertificateModal(false)}
           onSubmit={(certificateData) => {
             alert('Certificate issued successfully!');
             setShowIssueCertificateModal(false);
           }}
         />
       )}

       {showCourseDetailModal && selectedCourse && (
         <CourseDetailModal 
           course={selectedCourse}
           onClose={() => {
             setShowCourseDetailModal(false);
             setSelectedCourse(null);
           }}
         />
       )}

       {showLearnerProfileModal && selectedLearner && (
         <LearnerProfileModal 
           learner={selectedLearner}
           onClose={() => {
             setShowLearnerProfileModal(false);
             setSelectedLearner(null);
           }}
         />
       )}

       {showCertificateModal && selectedCertificate && (
         <CertificateModal 
           certificate={selectedCertificate}
           onClose={() => {
             setShowCertificateModal(false);
             setSelectedCertificate(null);
           }}
         />
       )}
     </div>
   );
 };

export default BloomDashboard;
